import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, addDoc, getDocs, updateDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebase/config';

const CLAIMS_COLLECTION = 'claims';

// Async thunks
export const fetchUserClaims = createAsyncThunk(
  'claimManagement/fetchUserClaims',
  async (userEmail, { rejectWithValue }) => {
    try {
      const claimsRef = collection(db, CLAIMS_COLLECTION);
      const q = query(
        claimsRef,
        where('email', '==', userEmail),
        orderBy('claimDate', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      const claims = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to regular dates for easier handling
        claimDate: doc.data().claimDate?.toDate(),
        expenseDate: doc.data().expenseDate?.toDate(),
        lastUpdated: doc.data().lastUpdated?.toDate()
      }));
      
      return claims;
    } catch (error) {
      console.error('Error fetching claims:', error);
      return rejectWithValue(error.message || 'Failed to fetch claims');
    }
  }
);

export const addNewClaim = createAsyncThunk(
  'claimManagement/addNewClaim',
  async ({ claimData, file }, { rejectWithValue }) => {
    try {
      let fileUrl = null;
      
      // Upload file if available
      if (file) {
        const fileExtension = file.name.split('.').pop();
        const storageRef = ref(storage, `claims/${Date.now()}.${fileExtension}`);
        const uploadResult = await uploadBytes(storageRef, file);
        fileUrl = await getDownloadURL(uploadResult.ref);
      }
      
      // Add claim document to Firestore
      const claimWithFile = {
        ...claimData,
        supportingDoc: fileUrl,
        claimDate: new Date(),
        lastUpdated: new Date()
      };
      
      const docRef = await addDoc(collection(db, CLAIMS_COLLECTION), claimWithFile);
      
      return {
        id: docRef.id,
        ...claimWithFile
      };
    } catch (error) {
      console.error('Error adding claim:', error);
      return rejectWithValue(error.message || 'Failed to add claim');
    }
  }
);

export const updateClaimStatus = createAsyncThunk(
  'claimManagement/updateClaimStatus',
  async ({ claimId, status, updatedBy }, { rejectWithValue }) => {
    try {
      const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
      
      await updateDoc(claimRef, {
        status,
        lastUpdated: new Date(),
        updatedBy
      });
      
      return {
        claimId,
        status,
        lastUpdated: new Date(),
        updatedBy
      };
    } catch (error) {
      console.error('Error updating claim status:', error);
      return rejectWithValue(error.message || 'Failed to update claim status');
    }
  }
);

export const updateUserClaim = createAsyncThunk(
  'claimManagement/updateUserClaim',
  async ({ claimId, claimData, file }, { rejectWithValue }) => {
    try {
      const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
      let supportingDoc = claimData.supportingDoc || null;

      if (file) {
        const fileExtension = file.name.split('.').pop();
        const storageRef = ref(storage, `claims/${Date.now()}.${fileExtension}`);
        const uploadResult = await uploadBytes(storageRef, file);
        supportingDoc = await getDownloadURL(uploadResult.ref);
      }

      const payload = {
        ...claimData,
        ...(supportingDoc ? { supportingDoc } : {}),
        amount: Number(claimData.amount || 0),
        expenseDate: claimData.expenseDate ? new Date(claimData.expenseDate) : claimData.expenseDate,
        lastUpdated: new Date(),
      };

      await updateDoc(claimRef, payload);

      return {
        claimId,
        payload,
      };
    } catch (error) {
      console.error('Error updating user claim:', error);
      return rejectWithValue(error.message || 'Failed to update claim');
    }
  }
);

const initialState = {
  claims: [],
  loading: false,
  error: null,
  addingClaim: false,
  addClaimError: null,
  updatingStatus: false,
  updateStatusError: null,
  updatingClaim: false,
  updateClaimError: null
};

const claimManagementSlice = createSlice({
  name: 'claimManagement',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.addClaimError = null;
      state.updateStatusError = null;
      state.updateClaimError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserClaims
      .addCase(fetchUserClaims.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserClaims.fulfilled, (state, action) => {
        state.claims = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserClaims.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch claims';
      })
      
      // addNewClaim
      .addCase(addNewClaim.pending, (state) => {
        state.addingClaim = true;
        state.addClaimError = null;
      })
      .addCase(addNewClaim.fulfilled, (state, action) => {
        state.claims = [action.payload, ...state.claims];
        state.addingClaim = false;
      })
      .addCase(addNewClaim.rejected, (state, action) => {
        state.addingClaim = false;
        state.addClaimError = action.payload || 'Failed to add claim';
      })
      
      // updateClaimStatus
      .addCase(updateClaimStatus.pending, (state) => {
        state.updatingStatus = true;
        state.updateStatusError = null;
      })
      .addCase(updateClaimStatus.fulfilled, (state, action) => {
        const { claimId, status, lastUpdated, updatedBy } = action.payload;
        
        state.claims = state.claims.map(claim => 
          claim.id === claimId 
            ? { ...claim, status, lastUpdated, updatedBy } 
            : claim
        );
        
        state.updatingStatus = false;
      })
      .addCase(updateClaimStatus.rejected, (state, action) => {
        state.updatingStatus = false;
        state.updateStatusError = action.payload || 'Failed to update claim status';
      })
      // updateUserClaim
      .addCase(updateUserClaim.pending, (state) => {
        state.updatingClaim = true;
        state.updateClaimError = null;
      })
      .addCase(updateUserClaim.fulfilled, (state, action) => {
        const { claimId, payload } = action.payload;
        state.claims = state.claims.map(claim =>
          claim.id === claimId
            ? { ...claim, ...payload }
            : claim
        );
        state.updatingClaim = false;
      })
      .addCase(updateUserClaim.rejected, (state, action) => {
        state.updatingClaim = false;
        state.updateClaimError = action.payload || 'Failed to update claim';
      });
  }
});

export const { clearErrors } = claimManagementSlice.actions;

export default claimManagementSlice.reducer; 